import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enTranslation from "../assets/locales/en/translation.json";
import esTranslation from "../assets/locales/es/translation.json";
import jpTranslation from "../assets/locales/jp/translation.json";

const resources = {
  en: {
    translation: enTranslation,
  },
  es: {
    translation: esTranslation,
  },
  ja: {
    translation: jpTranslation,
  },
};

const STORAGE_KEY = "i18nextLng";
const SUPPORTED_LANGUAGES = ["en", "es","ja"];
const GEOLOCATION_API_URL = "https://ipapi.co/json/";

const COUNTRY_LANGUAGE_MAP = {
  IN: "en",
  CN: "en",
  JP: "jp",
};

const normalizeLanguage = (languageCode) => {
  if (!languageCode) return null;
  const shortCode = languageCode.toLowerCase().split("-")[0];
  const normalizedCode = shortCode === "jp" ? "ja" : shortCode;
  return SUPPORTED_LANGUAGES.includes(normalizedCode) ? normalizedCode : null;
};

const getStoredLanguage = () => {
  try {
    return normalizeLanguage(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

const getCountryCode = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2500);
    const response = await fetch(GEOLOCATION_API_URL, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) return null;
    const data = await response.json();

    return (
      (data?.country_code || data?.countryCode || "").toUpperCase() || null
    );
  } catch {
    return null;
  }
};

const getLanguageFromCountry = async () => {
  const countryCode = await getCountryCode();
  if (!countryCode) return null;
  return normalizeLanguage(COUNTRY_LANGUAGE_MAP[countryCode]);
};

const resolveInitialLanguage = async () => {
  const storedLanguage = getStoredLanguage();
  if (storedLanguage) return storedLanguage;
  return getLanguageFromCountry();
};

export const initializeI18n = async () => {
  if (i18n.isInitialized) return i18n;

  const initialLanguage = await resolveInitialLanguage();

  await i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: "en",
      supportedLngs: SUPPORTED_LANGUAGES,
      lng: initialLanguage || undefined,
      debug: false,
      interpolation: {
        escapeValue: false,
      },
      detection: {
        order: ["localStorage", "navigator"],
        caches: ["localStorage"],
      },
    });

  return i18n;
};

export default i18n;
